import { FeatureFlagService, GlobalOptionsService } from '@common/ApiService'

const FLAG_SUCCESS = 'FLAG_SUCCESS'
const REALTIME_DATA = 'REALTIME_DATA'
const OUTWARD_JOURNEY_DATA = 'OUTWARD_JOURNEY_DATA'
const INWARD_JOURNEY_DATA = 'INWARD_JOURNEY_DATA'
const REFRESH_BOOKING = 'REFRESH_BOOKING'
const REFRESH_HANDOVER = 'REFRESH_HANDOVER'
const ADD_HANDOVER_BANNER = 'ADD_HANDOVER_BANNER'
const REMOVE_HANDOVER_BANNER = 'REMOVE_HANDOVER_BANNER'
const LOAD_HANDOVER_BANNERS_FROM_STORAGE = 'LOAD_HANDOVER_BANNERS_FROM_STORAGE'
const CLEAR_ALL_TIMEOUTS = 'CLEAR_ALL_TIMEOUTS'
const SET_BANNER_TIMEOUT = 'SET_BANNER_TIMEOUT'
const CLEAR_BANNER_TIMEOUT = 'CLEAR_BANNER_TIMEOUT'
const SET_ELECTRONIC_HANDOVER_BANNER_CHANNELS = 'SET_ELECTRONIC_HANDOVER_BANNER_CHANNELS'
const HANDOVER_BANNER_TIMEOUT = 120000
const STORAGE_KEY = 'HANDOVER_BANNERS'

const createBannerTimeout = (commit, id, delay) => {
  return setTimeout(() => {
    commit(REMOVE_HANDOVER_BANNER, id)
  }, delay)
}

const saveToStorage = (data, storageKey) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(data))
  } catch (error) {
    console.warn(`Failed to save ${storageKey} to localStorage:`, error)
  }
}

const loadFromStorage = (storageKey) => {
  try {
    const stored = localStorage.getItem(storageKey)
    if (!stored) return []

    const data = JSON.parse(stored)
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.warn(`Failed to load ${storageKey} from localStorage:`, error)
    return []
  }
}

const featureFlagsLoaded = (state) => {
  return new Promise((resolve, reject) => {
    let count = 1
    if (state.featureFlagsLoaded) return resolve(true)
    const checkFeatureFlagsLoaded = setInterval(
      (resolve, reject) => {
        if (state.featureFlagsLoaded) {
          clearInterval(checkFeatureFlagsLoaded)
          resolve(true)
        }
        count++
        if (count >= 10) {
          clearInterval(checkFeatureFlagsLoaded)
          reject(false)
        }
      },
      2000,
      resolve,
      reject
    )
  })
}

export const state = {
  featureFlags: [],
  featureFlagsLoaded: false,
  realTimeData: null,
  outwardjourneyOJP: null,
  inwardjourneyOJP: null,
  refreshBooking: false,
  refreshHandover: false,
  handoverBanners: [],
  activeTimeouts: {},
  electronicHandoverBannerChannels: [],
}

export const getters = {
  getFeatureFlagStatus: (state) => (flagName) => {
    featureFlagsLoaded(state).catch((data) => {
      console.error('featureFlagsLoaded:', data)
    })

    return state.featureFlags.some((flag) => flag.name === flagName && flag.enabled)
  },

  getHandoverBanners: (state) => {
    const now = Date.now()
    return state.handoverBanners
      .filter((banner) => now - banner.timestamp < HANDOVER_BANNER_TIMEOUT)
      .map((banner) => banner.id)
  },

  electronicHandoverBannerChannels: (state) => {
    return state.electronicHandoverBannerChannels
  },
}

export const mutations = {
  [FLAG_SUCCESS](state, data) {
    state.featureFlags = data
    state.featureFlagsLoaded = true
    return state
  },

  [REALTIME_DATA](state, data) {
    state.realTimeData = data
    return state
  },

  [OUTWARD_JOURNEY_DATA](state, data) {
    state.outwardjourneyOJP = data
  },

  [INWARD_JOURNEY_DATA](state, data) {
    state.inwardjourneyOJP = data
  },

  [REFRESH_BOOKING](state, data) {
    state.refreshBooking = data
  },

  [REFRESH_HANDOVER](state, data) {
    state.refreshHandover = data
  },

  [ADD_HANDOVER_BANNER](state, id) {
    const existingIndex = state.handoverBanners.findIndex((banner) => banner.id === id)
    if (existingIndex === -1) {
      const bannerData = { id, timestamp: Date.now() }
      state.handoverBanners.push(bannerData)

      saveToStorage(state.handoverBanners, STORAGE_KEY)
    }
  },

  [REMOVE_HANDOVER_BANNER](state, id) {
    const index = state.handoverBanners.findIndex((banner) => banner.id === id)
    if (index !== -1) {
      state.handoverBanners.splice(index, 1)
      saveToStorage(state.handoverBanners, STORAGE_KEY)
    }
  },

  [LOAD_HANDOVER_BANNERS_FROM_STORAGE](state) {
    const loadedBanners = loadFromStorage(STORAGE_KEY)
    const now = Date.now()

    state.handoverBanners = loadedBanners.filter((banner) => now - banner.timestamp < HANDOVER_BANNER_TIMEOUT)

    if (state.handoverBanners.length !== loadedBanners.length) {
      saveToStorage(state.handoverBanners, STORAGE_KEY)
    }
  },

  [SET_BANNER_TIMEOUT](state, { id, timeoutId }) {
    state.activeTimeouts[id] = timeoutId
  },

  [CLEAR_BANNER_TIMEOUT](state, id) {
    const timeoutId = state.activeTimeouts[id]
    if (timeoutId) {
      clearTimeout(timeoutId)
      delete state.activeTimeouts[id]
    }
  },

  [CLEAR_ALL_TIMEOUTS](state) {
    Object.values(state.activeTimeouts).forEach((timeoutId) => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    })
    state.activeTimeouts = {}
  },

  [SET_ELECTRONIC_HANDOVER_BANNER_CHANNELS](state, channels) {
    state.electronicHandoverBannerChannels = channels
  },
}

export const actions = {
  getFeatureFlags({ commit }, params) {
    return FeatureFlagService.getFlags().then((res) => {
      commit(FLAG_SUCCESS, res.data)
    })
  },

  sendRealTimeSocketMessage({ state, commit }, params) {
    if (params.journey_leg_ids.length === 0) {
      setTimeout(() => {
        commit(REALTIME_DATA, {
          train_service_updates: [],
        })
      }, 500)
    } else {
      return GlobalOptionsService.getRealTimeData(params)
        .then((response) => {
          if (response?.data) {
            commit(REALTIME_DATA, response.data)
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            window.location.reload()
          }
        })
    }
  },

  getRealTimeOJPJourneyDetail({ state, commit }, params) {
    const { type } = params
    return GlobalOptionsService.getRealTimeOJPData(params).then((response) => {
      if (response) {
        type === 'Outward' ? commit(OUTWARD_JOURNEY_DATA, response.data) : commit(INWARD_JOURNEY_DATA, response.data)
      }
    })
  },

  setRealTimeData({ commit }, params) {
    commit(REALTIME_DATA, params)
  },

  setOutwardJourneyData({ commit }, params) {
    commit(OUTWARD_JOURNEY_DATA, params)
  },

  setinwardJourneyData({ commit }, params) {
    commit(INWARD_JOURNEY_DATA, params)
  },

  setRefreshBookingModal({ commit }, params) {
    commit(REFRESH_BOOKING, params)
  },

  setRefreshHandoverModal({ commit }, params) {
    commit(REFRESH_HANDOVER, params)
  },

  addHandoverBanner({ commit, state }, id) {
    const existingBanner = state.handoverBanners.find((banner) => banner.id === id)

    if (!existingBanner) {
      commit(ADD_HANDOVER_BANNER, id)
      const timeoutId = createBannerTimeout(commit, id, HANDOVER_BANNER_TIMEOUT)
      commit(SET_BANNER_TIMEOUT, { id, timeoutId })
    } else if (!state.activeTimeouts[id]) {
      const now = Date.now()
      const timeElapsed = now - existingBanner.timestamp
      const remainingTime = HANDOVER_BANNER_TIMEOUT - timeElapsed

      if (remainingTime > 0) {
        const timeoutId = createBannerTimeout(commit, id, remainingTime)
        commit(SET_BANNER_TIMEOUT, { id, timeoutId })
      } else {
        // Banner has expired, remove it and add a new one
        commit(REMOVE_HANDOVER_BANNER, id)
        commit(ADD_HANDOVER_BANNER, id)
        const timeoutId = createBannerTimeout(commit, id, HANDOVER_BANNER_TIMEOUT)
        commit(SET_BANNER_TIMEOUT, { id, timeoutId })
      }
    }
  },

  removeHandoverBanner({ commit }, id) {
    commit(CLEAR_BANNER_TIMEOUT, id)
    commit(REMOVE_HANDOVER_BANNER, id)
  },

  loadHandoverBannersFromStorage({ commit, state }) {
    commit(LOAD_HANDOVER_BANNERS_FROM_STORAGE)

    const now = Date.now()

    state.handoverBanners.forEach((banner) => {
      const timeElapsed = now - banner.timestamp
      const remainingTime = HANDOVER_BANNER_TIMEOUT - timeElapsed

      if (remainingTime > 0 && !state.activeTimeouts[banner.id]) {
        const timeoutId = createBannerTimeout(commit, banner.id, remainingTime)
        commit(SET_BANNER_TIMEOUT, { id: banner.id, timeoutId })
      }
    })
  },

  clearAllTimeouts({ commit }) {
    commit(CLEAR_ALL_TIMEOUTS)
  },

  setElectronicHandoverBannerChannels({ commit }, channels) {
    commit(SET_ELECTRONIC_HANDOVER_BANNER_CHANNELS, channels)
  },
}
